import React from 'react';
import PropTypes from 'prop-types';
import { ModalView } from '../../../providers/modal.provider';
import { Box, Button, IconButton, MenuItem, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

const STATUS_OPTIONS = ['Not Started', 'In Progress', 'Blocked', 'Completed'];

const FeatureFlagEditModal = ({
  open,
  onClose,
  data,
  setData,
  type = 'definition',
  onSave,
}) => {
  console.log('Edit Modal Data:', open);
  const { t } = useTranslation();
  if (!open) return null;
  const handleChange = (key, value) => {
    setData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    onSave({
      key: data.key,
      ...data,
    });
  };

  return (
    <ModalView
      fader='show'
      onCloseEvent={onClose}
      centered
      size='md'
      overflowY='visible'
      disableClose={true}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 640,
          backgroundColor: 'background.paper',
          borderRadius: '16px',
          overflow: 'hidden',
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'background.paper',
          }}
        >
          <Box>
            <Typography
              variant='h6'
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              {type === 'deprecated' ? t('editDeprecatedFlag') : t('editFeatureFlag')}
            </Typography>
          </Box>

          <IconButton
            onClick={onClose}
            size='small'
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>

        {/* BODY */}
        <Box
          sx={{
            px: 3,
            py: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          {/* FLAG KEY */}
          <Box>
            <Typography
              variant='caption'
              sx={{
                display: 'block',
                mb: 1,
                fontWeight: 700,
                letterSpacing: 0.5,
                color: 'text.secondary',
              }}
            >
              {t('key')}
            </Typography>

            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderRadius: 2,
                backgroundColor: 'action.hover',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                  wordBreak: 'break-word',
                }}
              >
                {data?.key}
              </Typography>
            </Box>
          </Box>

          {/* DEFINITION */}
          <Box>
            <Typography
              variant='caption'
              sx={{
                display: 'block',
                mb: 1,
                fontWeight: 700,
                letterSpacing: 0.5,
                color: 'text.secondary',
              }}
            >
              {t('definition')}
            </Typography>

            <TextField
              fullWidth
              multiline
              minRows={4}
              maxRows={10}
              value={data?.description || ''}
              onChange={(e) =>
                setData({
                  ...data,
                  description: e.target.value,
                })
              }
              placeholder='Enter feature flag definition...'
              variant='outlined'
              sx={{
                '& .MuiOutlinedInput-root': {
                  alignItems: 'flex-start',
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                },
              }}
            />
          </Box>

          {/* DEPRECATED ONLY */}
          {type === 'deprecated' && (
            <>
              {/* STATUS */}
              <Box>
                <Typography
                  variant='caption'
                  sx={{
                    display: 'block',
                    mb: 1,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    color: 'text.secondary',
                  }}
                >
                  {t('status')}
                </Typography>

                <TextField
                  select
                  fullWidth
                  value={data?.status || ''}
                  onChange={(e) =>
                    setData({
                      ...data,
                      status: e.target.value,
                    })
                  }
                  SelectProps={{
                    displayEmpty: true,
                    MenuProps: {
                      disablePortal: true,
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                >
                  <MenuItem value='' disabled>
                    {t('selectStatus')}
                  </MenuItem>

                  {STATUS_OPTIONS.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {/* ASSIGNEE */}
              <Box>
                <Typography
                  variant='caption'
                  sx={{
                    display: 'block',
                    mb: 1,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    color: 'text.secondary',
                  }}
                >
                  {t('assignee')}
                </Typography>

                <TextField
                  fullWidth
                  value={data?.assignee || ''}
                  onChange={(e) =>
                    setData({
                      ...data,
                      assignee: e.target.value,
                    })
                  }
                  placeholder='Assign owner...'
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Box>
            </>
          )}
        </Box>

        {/* FOOTER */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1.5,
            backgroundColor: 'background.default',
          }}
        >
          <Button
            onClick={onClose}
            variant='outlined'
            sx={{
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {t('cancel')}
          </Button>

          <Button
            onClick={handleSave}
            variant='contained'
            sx={{
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
            }}
          >
            {t('update')}
          </Button>
        </Box>
      </Box>
    </ModalView>
  );
};

FeatureFlagEditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object,
  type: PropTypes.string,
  onSave: PropTypes.func,
  setData: PropTypes.func,
};

export default FeatureFlagEditModal;
